import { useMutation, useQuery } from '@apollo/client';
import RequiresAuth from '@website/components/auth/RequiresAuth';
import { HostProfileForm } from '@website/components/hosts/signupForms/hostProfileForm';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  HostProfileInput,
  GetHostUpdateHostProfileFieldsDocument,
  UpdateHostProfileDocument,
} from '@website/types/graphqlOperations';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import Router from 'next/router';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';

type EditHostProfileFormProps = {
  formData: HostProfileInput;
  urlSlug: string;
  hostID: string;
};

export function EditHostProfileForm({
  formData,
  urlSlug,
  hostID,
}: EditHostProfileFormProps) {
  const [updateHostProfile, { loading, error }] = useMutation(
    UpdateHostProfileDocument,
  );

  async function onSubmit(values: HostProfileInput) {
    const { data, errors } = await updateHostProfile({
      variables: {
        urlSlug,
        profile: values,
      },
    });
    if (!errors) {
      // use the newly update slug instead
      Router.push({
        pathname: '/hosts/dashboard/[hostURL]',
        query: {
          hostURL: data.updateHostProfile.urlSlug,
        },
      });
    }
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <RequiresAuth>
      <Container className="p-4">
        <Breadcrumb className="py-3">
          <Link href={`/hosts/dashboard/${urlSlug}`} passHref legacyBehavior>
            <Breadcrumb.Item href="">Host Dashboard</Breadcrumb.Item>
          </Link>
          <Breadcrumb.Item>Edit</Breadcrumb.Item>
          <Breadcrumb.Item active>Host Profile</Breadcrumb.Item>
        </Breadcrumb>
        <Row>
          <Col>
            <h3 className="h3 mb-5">Edit your Host Details</h3>
            <HostProfileForm
              onSubmit={onSubmit}
              isLoading={loading}
              values={formData}
              hostID={hostID}
            />
          </Col>
        </Row>
      </Container>
    </RequiresAuth>
  );
}

type EditHostProfilePageProps = {
  urlSlug: string;
};

export default function EditHostProfilePage({
  urlSlug,
}: EditHostProfilePageProps) {
  const { loading, error, data } = useQuery(
    GetHostUpdateHostProfileFieldsDocument,
    {
      variables: {
        urlSlug,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  if (error) {
    return <ErrorPage />;
  }

  if (!data || loading) {
    return <LoadingPage />;
  }

  const { id: hostID, ...formData } = data.hostByURL;

  return (
    <EditHostProfileForm
      urlSlug={urlSlug}
      formData={formData as HostProfileInput}
      hostID={hostID}
    />
  );
}

export const getServerSideProps: GetServerSideProps<
  EditHostProfilePageProps
> = async ({ query }) => {
  // use this to pass url params as props to the page, this prevents the weirdness
  // around the useRouter hook where you have to check whether the query params
  // are set.
  const { hostURL } = query;
  return {
    props: {
      urlSlug: hostURL as string,
    },
  };
};
